import React, { useState } from 'react';
import { ChatStateProvider } from '../../contexts/ChatStateProvider';
import ChatInterface from './ChatInterface';
import ArtifactsPanel from './ArtifactsPanel';
import ErrorBoundary from '../ui/ErrorBoundary';

const ChatWithArtifacts = ({ formState, onFormUpdate, sessionId, checkpointContext }) => {
  const [artifacts, setArtifacts] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const addArtifact = (artifact) => {
    setArtifacts(prev => {
      // Dedupe checkpoint artifacts by experiment_id + phase
      if (artifact.type === 'Checkpoint' && artifact.data) {
        const isDupe = prev.some(a =>
          a.type === 'Checkpoint' &&
          a.data?.experiment_id === artifact.data.experiment_id &&
          a.data?.phase === artifact.data.phase
        );
        if (isDupe) {
          console.log('Skipping duplicate checkpoint artifact');
          return prev;
        }
      }
      return [...prev, artifact];
    });
    setSidebarOpen(true);
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  return (
    <div className="flex h-full w-full max-w-none">
      {/* Fixed Artifact Panel Buttons - Always visible */}
      <div className={`fixed top-20 z-30 transition-all duration-500 ease-out ${sidebarOpen ? 'right-[calc(50%+1rem)]' : 'right-4'}`}>
        {sidebarOpen ? (
          <button
            onClick={toggleSidebar}
            className="p-1.5 rounded-lg text-stone-400 hover:text-stone-600 hover:bg-stone-100/50 dark:text-slate-500 dark:hover:text-slate-300 dark:hover:bg-slate-800/50 transition-all bg-stone-50/90 dark:bg-slate-900/90 backdrop-blur-md shadow-sm"
            aria-label="Hide sidebar"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        ) : (
          <div className="flex flex-col gap-2">
            {/* Charts button */}
            <button
              onClick={toggleSidebar}
              className="p-3 rounded-xl bg-white/80 dark:bg-slate-800/80 text-stone-500 hover:text-stone-700 dark:text-slate-400 dark:hover:text-slate-200 shadow-sm hover:shadow-md transition-all duration-300 border border-stone-200/50 dark:border-slate-700/50 backdrop-blur-sm relative"
              aria-label="Show charts"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              {artifacts.filter(a => a.type === 'Chart').length > 0 && (
                <div className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-blue-500 text-white text-xs font-semibold rounded-full flex items-center justify-center shadow-sm border-2 border-white dark:border-slate-900">
                  {artifacts.filter(a => a.type === 'Chart').length > 9 ? '9+' : artifacts.filter(a => a.type === 'Chart').length}
                </div>
              )}
            </button>

            {/* Tables button */}
            <button
              onClick={toggleSidebar}
              className="p-3 rounded-xl bg-white/80 dark:bg-slate-800/80 text-stone-500 hover:text-stone-700 dark:text-slate-400 dark:hover:text-slate-200 shadow-sm hover:shadow-md transition-all duration-300 border border-stone-200/50 dark:border-slate-700/50 backdrop-blur-sm relative"
              aria-label="Show tables"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              {artifacts.filter(a => a.type === 'Table').length > 0 && (
                <div className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-blue-500 text-white text-xs font-semibold rounded-full flex items-center justify-center shadow-sm border-2 border-white dark:border-slate-900">
                  {artifacts.filter(a => a.type === 'Table').length > 9 ? '9+' : artifacts.filter(a => a.type === 'Table').length}
                </div>
              )}
            </button>
          </div>
        )}
      </div>

      {/* Chat Panel - takes remaining space */}
      <div className={`flex-1 w-0 min-w-0 flex flex-col transition-all duration-500 ease-out relative ${sidebarOpen ? 'mr-[50%]' : ''}`}>
        <div className={`w-full flex-1 ${sidebarOpen ? '' : 'max-w-6xl mx-auto px-12 py-8'}`}>
          <ErrorBoundary fallbackMessage="Failed to load chat interface. Please refresh the page.">
            <ChatStateProvider
              formState={formState}
              onFormUpdate={onFormUpdate}
              onArtifactCreated={addArtifact}
              sessionId={sessionId}
              expandedMode={sidebarOpen}
              checkpointContext={checkpointContext}
            >
              <ChatInterface expandedMode={sidebarOpen} checkpointContext={checkpointContext} />
            </ChatStateProvider>
          </ErrorBoundary>
        </div>
      </div>

      {/* Paper-like Sidebar - animated expand/collapse */}
      <div className={`
        ${sidebarOpen ? 'w-1/2' : 'w-0'} 
        flex flex-col transition-all duration-500 ease-out
        ${sidebarOpen ? 'bg-stone-50/90 dark:bg-slate-900/90 border-l border-stone-200/70 dark:border-slate-700/70' : ''}
        backdrop-blur-md
        fixed right-0 top-0 h-full
        z-20
        overflow-hidden
      `}>
        {/* Subtle paper texture */}
        {sidebarOpen && (
          <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-stone-100/10 dark:from-slate-800/20 dark:to-slate-900/10 pointer-events-none" />
        )}
        
        {/* Expanded View Content */}
        {sidebarOpen && (
          <div className="flex flex-col h-full relative z-10">
            {/* Artifacts Header */}
            <div className="px-5 py-4">
              <div className="flex items-center justify-center gap-4">
                {/* Charts badge - always show */}
                <div className="flex items-center gap-1.5 px-4 py-2.5 bg-white/90 text-stone-800 shadow-sm border border-stone-200/50 dark:bg-slate-700/80 dark:text-slate-100 dark:border-slate-600/50 rounded-xl">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  <span>Charts</span>
                  {artifacts.filter(a => a.type === 'Chart').length > 0 && (
                    <span className="bg-stone-200 dark:bg-slate-600 text-stone-700 dark:text-slate-300 text-xs font-semibold px-2 py-0.5 rounded-full min-w-[1.25rem] text-center">
                      {artifacts.filter(a => a.type === 'Chart').length}
                    </span>
                  )}
                </div>

                {/* Tables badge - always show */}
                <div className="flex items-center gap-1.5 px-4 py-2.5 bg-white/90 text-stone-800 shadow-sm border border-stone-200/50 dark:bg-slate-700/80 dark:text-slate-100 dark:border-slate-600/50 rounded-xl">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  <span>Tables</span>
                  {artifacts.filter(a => a.type === 'Table').length > 0 && (
                    <span className="bg-stone-200 dark:bg-slate-600 text-stone-700 dark:text-slate-300 text-xs font-semibold px-2 py-0.5 rounded-full min-w-[1.25rem] text-center">
                      {artifacts.filter(a => a.type === 'Table').length}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Content Area with Paper Feel */}
            <div className="flex-1 overflow-hidden px-5 pb-5">
              <div className="h-full bg-white/70 dark:bg-slate-800/50 rounded-xl border border-stone-200/50 dark:border-slate-700/50 backdrop-blur-sm shadow-sm overflow-y-auto">
                <div className="h-full">
                  {artifacts.length > 0 ? (
                    <ErrorBoundary fallbackMessage="Failed to load visualizations.">
                      <ArtifactsPanel 
                        artifacts={artifacts} 
                        onClose={toggleSidebar}
                        hideHeader={true}
                      />
                    </ErrorBoundary>
                  ) : (
                    <div className="h-full flex flex-col items-center justify-center p-8">
                      <div className="w-14 h-14 bg-stone-100 dark:bg-slate-700 rounded-2xl flex items-center justify-center mb-5 shadow-sm">
                        <svg className="w-7 h-7 text-stone-400 dark:text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                      </div>
                      <h3 className="text-base font-medium text-stone-800 dark:text-slate-200 mb-2">
                        No charts yet
                      </h3>
                      <p className="text-sm text-stone-500 dark:text-slate-400 text-center leading-relaxed max-w-sm">
                        Visualizations from your data analysis will appear here
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatWithArtifacts;